<template>
  <div class="overlay" @click.self="closePopup">
    <div class="popup">
      <h2>{{ $t('SendEmail.SendEmail') }}</h2>
      <div class="form-group">
        <label>{{ $t('SendEmail.XML') }}</label>
        <select class="operatorSelect" v-model="selectedXML">
              <option value="system"> {{ $t('SendEmail.FromSystem') }}</option>
              <option value="custom">{{ $t('SendEmail.UseCustom') }}</option>
              <option value="max">{{ $t('SendEmail.Max') }}</option>
            </select>
        <div v-if="selectedXML === 'custom'" class="upload-section">
          <input
            type="file"
            id="xmlUpload"
            @change="handleFileUpload"
            accept=".xml"
            class="hidden-file-input"
          />
          <button @click.prevent="triggerFileUpload" class="upload-button">
            <span class="material-symbols-outlined">upload</span> XML
          </button>
          <span v-if="userXML" class="info-message">{{ xmlTitle}}</span>
        </div>
      </div>

      <div class="form-group">
        <label for="emailAddress">{{ $t('SendEmail.RecipientEmail') }}</label>
        <input
          type="email"
          id="emailAddress"
          v-model="emailAddress"
          placeholder="Enter recipient email"
          :class="{ invalid: emailInvalid }"
        />
        <span v-if="emailInvalid" class="error-message">{{ $t('SendEmail.ErrorMessage') }}.</span>
      </div>

      <div class="button-group">
        <button @click="sendEmail" :disabled="emailInvalid">{{ $t('SendEmail.Send') }}</button>
        <button @click="closePopup">{{ $t('SendEmail.Cancel') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { convertJsonToHtml } from '@/utils/convertJsonToHtml';

export default {
  name: 'SendEmail',
  data() {
    return {
      emailAddress: '',
      emailInvalid: false,
      selectedXML: 'system',
      userXML: null, // Variable to store user-uploaded XML
      currentXML: null, // Store current XML temporarily
      xmlTitle: ''
    };
  },
  computed: {
    ...mapState(['emailData']),
    ...mapGetters(['getEmailTitle', 'getXMLFile']),
    currentEmailTitle() {
      return this.getEmailTitle || 'NewTemplate';
    },
  },
  methods: {
    ...mapActions(['updateXmlFile']),
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.userXML = e.target.result; // Store the XML content
        };
        reader.readAsText(file);
      }
    },
    triggerFileUpload() {
      document.getElementById('xmlUpload').click();
    },
    validateEmail() {
      const validEmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      this.emailInvalid = !validEmailPattern.test(this.emailAddress);
    },
    async sendEmail() {
      this.validateEmail();
      if (!this.emailInvalid) {
        let htmlContent;
        if (this.selectedXML === 'custom' && this.userXML) {
          this.currentXML = this.getXMLFile; // Store the current XML
          this.updateXmlFile(this.userXML); // Temporarily update the XML with the user file
          htmlContent = convertJsonToHtml(this.emailData, true);
          this.updateXmlFile(this.currentXML); // Revert to the original XML
        } else if (this.selectedXML === 'system') {
          htmlContent = convertJsonToHtml(this.emailData, true);
        } else {
          htmlContent = convertJsonToHtml(this.emailData, true, true);
        }

        try {
          await this.sendMailRequest(this.emailAddress, this.currentEmailTitle, htmlContent);
          alert('Email sent successfully!');
        } catch (error) {
          console.error('Failed to send email:', error);
          alert('Failed to send email. Please try again.');
        }
        this.closePopup();
      }
    },
    async sendMailRequest(recipient, subject, htmlContent) {
      const servers = [
        'https://html-creator.demo.cartago.com',
        'https://html-creator.demo.cartago.com',
        'http://demo.cartago.com:5050'
      ];

      let success = false;

      for (const server of servers) {
        try {
          const response = await fetch(`${server}/api/send-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              recipient,
              subject,
              htmlContent,
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to send email');
          }

          console.log('Email successfully sent via:', server);
          success = true;
          break;
        } catch (error) {
          console.error(`Failed to send email via ${server}:`, error);
        }
      }

      if (!success) {
        throw new Error('Failed to send email via all available servers');
      }
    },
    closePopup() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup {
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 400px;
  text-align: left;
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.xml-options {
  display: flex;
  gap: 15px;
  margin-bottom: 10px;
}

.upload-section {
  margin-top: 10px;
}

.hidden-file-input {
  display: none;
}


.info-message {
  color: green;
  font-size: 12px;
  margin-top: 5px;
}

input {
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
}

.invalid {
  border-color: red;
}

.error-message {
  color: red;
  font-size: 12px;
}

.button-group {
  display: flex;
  justify-content: space-between;
}

button {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

button[disabled] {
  background: #ccc;
  cursor: not-allowed;
}

button:not([disabled]) {
  background: var(--primary-color);
  color: white;
}

button:not([disabled]):hover {
  background: var(--primary-color);
}

.upload-button {
  cursor: pointer;
  padding: 10px 20px;
  color: white;
  background-color: #ccc !important;
  border: none;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  font-size: 16px;
}

.upload-button span {
  margin-right: 8px;
  font-size: 24px;
}

.upload-button:hover{
  cursor: pointer;
  background-color: darkgray;  
}

</style>
